import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import LoadStep from './LoadStep';
import SupplierStep from './SupplierStep';
import SummaryDetails from '../components/wizard/SummaryDetails';
import SummaryStep from './SummaryStep';
import ConfirmationStep from './ConfirmationStep';
import { useViewLoadQuery } from '../loadsEndpoints';
import { initialLoadState, initialSupplierState } from '../components/wizard/initialState';

const initialState = {
  ...initialLoadState,
  ...initialSupplierState,
};

const pickLoadState = R.pick([
  'deliveryEndDateTime',
  'deliveryStartDateTime',
  'driver',
  'location',
  'poNumber',
  'trailer',
]);

const EditLoadWizard = ({ loadId, onClose }) => {
  const [step, setStep] = useState(1);
  const [loadState, setLoadState] = useState(initialState);
  const [draftDialogOpened, setDraftDialogOpened] = useState(false);
  const [saved, setSaved] = useState(false);

  const { data, isFetching, isSuccess } = useViewLoadQuery({ loadId });

  const next = () => {
    setStep(R.inc);
  };
  const prev = () => {
    setStep(R.dec);
  };
  const reset = () => {
    setStep(1);
    setLoadState(({ driver, location, trailer, compartments }) => ({
      ...initialState,
      compartments,
      driver,
      location,
      trailer,
    }));
  };

  const handleClose = () => {
    const zipped = R.zipWith(
      R.equals,
      R.values(pickLoadState(initialLoadState)),
      R.values(pickLoadState(loadState))
    );

    const step1Completed = R.all(R.equals(false), zipped);
    const isDraft = R.either(R.equals('Draft'), R.equals('DRAFT'))(loadState.status);

    if (saved || !step1Completed || !isDraft || R.equals(loadState, data)) {
      onClose();
    } else {
      setDraftDialogOpened(true);
    }
  };

  const handleCloseModal = () => setDraftDialogOpened(false);

  const handleSaved = () => {
    setSaved(true);
  };

  const stepProps = {
    loadState,
    next,
    onClose: handleClose,
    prev,
    reset,
    setLoadState,
  };

  useEffect(() => {
    if (isSuccess) setLoadState(data);
  }, [data, isSuccess]);

  return (
    <Drawer anchor="right" onClose={handleClose} open>
      <Box height={1} width={390}>
        {step === 1 && <LoadStep {...stepProps} isFetchingLoad={isFetching} />}
        {step === 2 && <SupplierStep {...stepProps} />}
        {step === 3 && (
          <SummaryStep {...stepProps}>
            <SummaryDetails loadState={loadState} />
          </SummaryStep>
        )}
        {step === 4 && (
          <ConfirmationStep {...stepProps} onSaved={handleSaved}>
            <SummaryDetails loadState={loadState} />
          </ConfirmationStep>
        )}
      </Box>
      {draftDialogOpened && (
        <Dialog
          maxWidth="xs"
          open
          slotProps={{
            paper: { elevation: 10 },
          }}
          sx={{ '& .MuiDialog-paper': { height: 200, width: 380 } }}
        >
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogContent>
            Are you sure you want to leave this page? You have unsaved changes.
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={onClose} variant="outlined">
              Continue
            </Button>
            <Button onClick={handleCloseModal}>Keep Editing</Button>
          </DialogActions>
        </Dialog>
      )}
    </Drawer>
  );
};

EditLoadWizard.propTypes = {
  loadId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditLoadWizard;
